import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Form, Input } from 'antd';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/shallow';
import { API } from '@services/api.service';
import { OPERATION } from '@/constants';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useNotify } from '@/hooks';
import { cn } from '@/libs/utils';
import { QUERY_KEY } from '@/shared/constants';
import { useSalaryStore } from '@/store/useSalaryStore';
import { isValidDateArray } from '@/utils/dateUtils';
import {
  DATE_FORMATS,
  formatDateForBackend,
  getNextWorkday,
  getSelectedWorkdaysCount,
  isEmployeeRequestFormDisabled,
  isShiftLeavesEffective,
} from '@/utils/formUtils';
import CustomDatePicker from '../input/CustomDatePicker';
import CustomRangePicker from '../input/CustomRangePicker';
import FormActionButton from '../input/FormActionButton';

export default function NonPaidLeaveForm({
  formData,
  typeOfLeave,
  employeeId,
  handleRefetch,
}) {
  const { t } = useTranslation(['menus', 'forms', 'messages']);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const [markedDates, setMarkedDates] = useState([]);
  const notification = useNotify();

  const { toggle, setToggle } = useSalaryStore(
    useShallow((state) => ({
      toggle: state.toggle,
      setToggle: state.setToggle,
    }))
  );

  const handleCancelModal = () => {
    if (toggle.leaveExtender) {
      setToggle('leaveExtender', false);

      // * Refetch after submitted
      handleRefetch();
    }
  };

  // * Form instance
  const [form] = Form.useForm();

  // * Initial form data
  const initialValues = {
    dateOfLeave: [],
    dateOfRequestCreate: null,
  };

  const isCompanyBilingual =
    formData?.company?.applicationSettings?.typeOfHr?.key === 'bilingual';

  // * Check if company allows weekend leaves
  const allowWeekendLeaves = isShiftLeavesEffective(formData, formData?.company);
  const publicHolidays = useMemo(
    () => formData?.company?.publicHolidays || [],
    [formData?.company?.publicHolidays]
  );

  // * Dynamic form values
  const dateOfLeaveValues = Form.useWatch('dateOfLeave', form);
  const isDisabledSubmit = isValidDateArray(
    dateOfLeaveValues?.map((d) => (d ? d.toISOString() : null))
  );

  // * Enabled fetch data if ok
  const isEnabled = (queryParams, employeeId) => {
    const { action, requestId } = queryParams;
    const hasAction = [OPERATION.view, OPERATION.edit].includes(action);
    return !!requestId && !!employeeId && !!hasAction;
  };

  // * Fetch data for (view or update)
  const { data: requestData } = useQuery({
    queryKey: [
      QUERY_KEY.request,
      employeeId,
      queryParams.action,
      queryParams.requestId,
    ],
    queryFn: async () => {
      return await API.requests.getRequestById(queryParams.requestId);
    },
    enabled: isEnabled(queryParams, employeeId),
    retry: false,
  });

  // * Show submitted action and disable input fields if not `ACCEPTED` or not `edit` mode
  const hasSubmitted = isEmployeeRequestFormDisabled(
    queryParams?.action ?? '',
    requestData
  );

  // * Set `initial data` from (update or view)
  useEffect(() => {
    if (requestData) {
      const { dateFrom, dateTo, dateOfCreation, reasonOfLeave } = requestData;

      form.setFieldsValue({
        reasonOfLeaveText: reasonOfLeave,
        dateOfLeave: [dayjs(dateFrom), dayjs(dateTo)],
        dateOfRequestCreate: dateOfCreation ? dayjs(dateOfCreation) : null,
      });
    }
  }, [form, requestData]);

  // * Calculation (Total Selected Days)
  const totalSelectedDays = useMemo(() => {
    return getSelectedWorkdaysCount(dateOfLeaveValues, allowWeekendLeaves, publicHolidays);
  }, [dateOfLeaveValues, allowWeekendLeaves, publicHolidays]);

  // * Calculation (Next day of working)
  const nextWorkingDay = useMemo(() => {
    return getNextWorkday(dateOfLeaveValues, allowWeekendLeaves, publicHolidays);
  }, [dateOfLeaveValues, allowWeekendLeaves, publicHolidays]);

  // * Handle reset form
  const onReset = useCallback(() => {
    form.resetFields();
  }, [form]);

  // * After submitted form (create or edit) redirect to `route=hr-documents`
  const goToHrDocuments = useCallback(() => {
    setQueryParams({ ['route']: 'hr-documents', ['action']: undefined });
  }, [setQueryParams]);

  // * Reset form if not ACTION=(view | edit) from query params of url
  useEffect(() => {
    if (!queryParams.action) {
      onReset();
    }
  }, [queryParams.action, onReset]);

  // * Handle submit form (Create or Edit)
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload) => {
      if (queryParams.action === 'edit') {
        return await API.requests.updateRequestById(
          queryParams.requestId,
          payload
        );
      }

      return await API.requests.createNewRequest(payload);
    },
    onSuccess: (response) => {
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message,
      });

      // * Close modal if open your `LeaveExtenderModal` form modal
      handleCancelModal();

      // * Reset form fields
      onReset();

      // * Redirect to `route=hr-documents` after submitted form
      goToHrDocuments();
    },
    onError: (error) => {
      const errorMsg = error.response.data.error || error.message;

      notification.error({
        message: t('error', { ns: 'messages' }),
        description: errorMsg,
      });
    },
  });

  const getMarkedDates = (items) => {
    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      ...item,
      disabled: true,
    }));
  };

  const { mutateAsync: mutateAsyncDate } = useMutation({
    mutationFn: async (payload) => {
      return await API.requests.getUsedDates(payload);
    },
    onSuccess: (response) => {
      const markedDates = getMarkedDates(response.items);

      setMarkedDates(markedDates);
    },
    onError: (error) => {
      const errorMsg = error.response.data.error || error.message;

      notification.error({
        message: t('error', { ns: 'messages' }),
        description: errorMsg,
      });
    },
  });

  const handleMonthChange = useCallback(
    (start, end) => {
      const payload = {
        employeeId: formData._id,
        startDate: formatDateForBackend(start, DATE_FORMATS.STANDARD),
        endDate: formatDateForBackend(end, DATE_FORMATS.STANDARD),
      };

      mutateAsyncDate(payload);
    },
    [formData._id, mutateAsyncDate]
  );

  useEffect(() => {
    if (!formData?._id) return;

    const start = dayjs().startOf('month');
    const end = dayjs().add(1, 'month').endOf('month');

    handleMonthChange(start, end);
  }, [formData?._id, handleMonthChange]);

  const onFinish = async (values) => {
    const { _id: employeeId, company, firstName, lastName } = formData;

    const {
      dateOfLeave,
      dateOfRequestCreate,
      reasonOfLeaveText,
      reasonOfLeaveTextEng,
    } = values;

    const [dateFrom, dateTo] = dateOfLeave;

    const payload = {
      employee: employeeId,
      company: company._id,
      firstName,
      lastName,
      reasonOfLeave: reasonOfLeaveText,
      reasonOfLeaveEng: reasonOfLeaveTextEng,
      numOfDays: totalSelectedDays.toString(),
      firstDayBack: formatDateForBackend(nextWorkingDay, DATE_FORMATS.STANDARD),
      dateFrom: formatDateForBackend(dateFrom, DATE_FORMATS.STANDARD),
      dateTo: formatDateForBackend(dateTo, DATE_FORMATS.STANDARD),
      dateOfCreation: formatDateForBackend(
        dateOfRequestCreate,
        DATE_FORMATS.STANDARD
      ),
      typeOfLeave,
      override: false,
    };

    if (!hasSubmitted) {
      await mutateAsync(payload);
    } else {
      console.warn('Please check your hasSubmitted value', hasSubmitted);
    }
  };

  return (
    <Form
      form={form}
      name="employee_request-nonPaidLeave-form"
      initialValues={initialValues}
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      className={cn('form grid gap-4')}
    >
      <div className="rounded-md border bg-zinc-100 p-4">
        <>
          <Form.Item
            label={t('reasonOfLeaveText', { ns: 'forms' })}
            name="reasonOfLeaveText"
            rules={[
              {
                required: true,
                message: t('formValidation.required', { ns: 'forms' }),
              },
            ]}
          >
            <Input.TextArea
              rows={2}
              placeholder={t('reasonOfLeaveText', { ns: 'forms' })}
              disabled={hasSubmitted}
            />
          </Form.Item>

          <hr className="mb-5" />
        </>

        {isCompanyBilingual && (
          <>
            <Form.Item
              label={t('reasonOfLeaveTextEng', { ns: 'forms' })}
              name="reasonOfLeaveTextEng"
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
              ]}
            >
              <Input.TextArea
                rows={2}
                placeholder={t('reasonOfLeaveTextEng', { ns: 'forms' })}
                disabled={hasSubmitted}
              />
            </Form.Item>

            <hr className="mb-5" />
          </>
        )}

        <div className="grid gap-x-4 md:grid-cols-2">
          <Form.Item
            label={t('dateOfLeave', { ns: 'forms' })}
            name="dateOfLeave"
            rules={[
              {
                required: true,
                message: t('formValidation.selectDate', { ns: 'forms' }),
              },
            ]}
          >
            <CustomRangePicker
              form={form}
              markedDates={markedDates}
              onMonthChange={handleMonthChange}
              disabled={hasSubmitted}
              allowWeekendLeaves={allowWeekendLeaves}
              publicHolidays={publicHolidays}
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item label={t('numberOfDays', { ns: 'forms' })}>
              <div
                className={cn(
                  'flex h-8 w-full cursor-not-allowed items-center rounded-md border border-zinc-300 bg-zinc-100 px-3',
                  hasSubmitted ? 'bg-zinc-200 opacity-75' : ''
                )}
              >
                <p>{totalSelectedDays || '~'}</p>
              </div>
            </Form.Item>

            <Form.Item label={t('firstDayBack', { ns: 'forms' })}>
              <div
                className={cn(
                  'flex h-8 w-full cursor-not-allowed items-center rounded-md border border-zinc-300 bg-zinc-100 px-3',
                  hasSubmitted ? 'bg-zinc-200 opacity-75' : ''
                )}
              >
                <p>
                  {nextWorkingDay
                    ? dayjs(nextWorkingDay).format(DATE_FORMATS.DISPLAY_DOTS)
                    : '~'}
                </p>
              </div>
            </Form.Item>
          </div>
        </div>
        <Form.Item
          label={t('dateOfRequestCreation', { ns: 'forms' })}
          name="dateOfRequestCreate"
        >
          <CustomDatePicker form={form} disabled={hasSubmitted} />
        </Form.Item>
      </div>

      {!hasSubmitted && (
        <FormActionButton
          isPending={isPending}
          isDisabled={!isDisabledSubmit}
          onReset={onReset}
        />
      )}
    </Form>
  );
}
